import { Spinner2 } from 'components';
import NoDataFound from 'components/NoDataFound';
import { ITabConfig, TabsControl } from 'components/TabsControl/TabsControl';
import { Row, Col, Button } from 'react-bootstrap';
import BootstrapTable from 'react-bootstrap-table-next';
import { useTranslation } from 'react-i18next';
import i18n from 'translations';
import React, { useState } from 'react';
import { LICENSE_CONTENT_SEARCH, LICENSE_TABS } from 'constants/constants';
import { HD_TICKET_FORMAT } from 'utils/common';
import { getLocalStorageItem } from 'utils/localStorage';
import { useQuery } from 'hooks/query';
import { useHistory, useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { ILicenseContent, licenseContentThunk } from 'thunks/license-content';
import { licenseContentSlice } from 'slices';
import { LicenseInfoDetails } from './LicenseDetails';
import { LicenseInfoSummary } from './LicenseSummary';
import { columns } from './LicenseSummary/columns';
import { OrderedLicenses } from './OrderedLicenses';

const tabs: ITabConfig[] = [
  {
    eventKey: LICENSE_TABS.ACTIVE,
    title: i18n.t('licenseContent.tabActive'),
    children: <LicenseInfoSummary />,
  },
  {
    eventKey: LICENSE_TABS.ORDERED,
    title: i18n.t('licenseContent.tabOrdered'),
    children: <OrderedLicenses />,
  },
  {
    eventKey: LICENSE_TABS.DETAILS,
    title: i18n.t('licenseContent.tabDetails'),
    children: <LicenseInfoDetails />,
  },
];

export const LicenseContainer = () => {
  const { mainProductInfo } = useAppSelector(
    (state) => state.licenseContent.data
  );

  const error = useAppSelector((state) => state.licenseContent.error);
  const dispatch = useAppDispatch();
  const [search, setSearch] = useState('');
  const { t } = useTranslation();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const getPageUrl = () => {
    return `/v1/ticket-licenses-details`;
  };
  const query = useQuery();
  const history = useHistory();

  function validFormat(ticketId: string) {
    return HD_TICKET_FORMAT.test(ticketId);
  }

  const handleChange = (e: any) => {
    setSearch(e.target.value);
  };

  const fetchData = async (ticketId: string) => {
    setIsProcessing(true);
    localStorage.setItem(LICENSE_CONTENT_SEARCH, ticketId);
    const payload: ILicenseContent = {
      url: getPageUrl(),
      ticketId,
      baseURL: process.env.REACT_APP_ACTIVITY_BASE_URL,
    };

    await dispatch(licenseContentThunk(payload));
    setIsProcessing(false);
  };

  const location = useLocation();
  const searchQueryString = location.search; // returns the URL query String
  const params = new URLSearchParams(searchQueryString);
  const licenseKey = params.get('licenseKey');

  React.useEffect(() => {
    if (licenseKey) {
      dispatch(licenseContentSlice.actions.resetData());
      setSearch(licenseKey);
      fetchData(licenseKey);
    }
  }, [licenseKey]);

  const searchLicense = async () => {
    dispatch(licenseContentSlice.actions.resetData());
    if (validFormat(search)) {
      await fetchData(search);
    } else {
      dispatch(
        licenseContentSlice.actions.setError({
          message: t('licenseContent.invalid'),
        })
      );
      dispatch(licenseContentSlice.actions.resetData());
    }
  };

  const handleKeyDown = (e: any) => {
    if (e.key === 'Enter') {
      searchLicense();
    }
  };
  const clickSearch = () => {
    searchLicense();
  };

  React.useEffect(() => {
    const searchQuery =
      getLocalStorageItem(LICENSE_CONTENT_SEARCH) ||
      params.get('licenseKey') ||
      search;
    if (searchQuery) {
      if (validFormat(searchQuery)) {
        setSearch(searchQuery);
        fetchData(searchQuery);
      } else {
        dispatch(
          licenseContentSlice.actions.setError({
            message: t('licenseContent.invalid'),
          })
        );
      }
    } else {
      dispatch(licenseContentSlice.actions.resetData());
      setSearch('');
      dispatch(licenseContentSlice.actions.setError(undefined));
    }
  }, []);

  const handleTabChange = (eventKey: any) => {
    let appendedKey = '';
    if (licenseKey) {
      appendedKey = `&licenseKey=${licenseKey}`;
    }
    history.push(`${location.pathname}?tab=${eventKey}${appendedKey}`);
  };

  return (
    <>
      <Row className="m-0 justify-content-center">
        <Col md={12}>
          <Row className="m-0">
            <Col md={12} className="mx-auto justify-content-between">
              <Row className="m-0 d-flex justify-content-between mb-2">
                <Col md={4} />
                <Col md={8}>
                  <small
                    className="fs-10"
                    style={{ color: !error ? 'grey' : 'red' }}
                  >
                    {t('licenseContent.format')}: XXXXX-XXXXX-XXXXX-XXXXX-XXXXX
                  </small>
                  <br />
                  {!!error && (
                    <small className="fs-12" style={{ color: 'red' }}>
                      {error.message}
                    </small>
                  )}
                </Col>
              </Row>

              <Row className="m-0 mb-3">
                <Col md={4} className="justify-content-between d-flex">
                  <h1 className="mt-12 mb-8 fs-24">
                    {t('licenseContent.heading')}
                  </h1>
                </Col>
                <Col md={6} className="justify-content-end">
                  <Row className="m-0 mb-8">
                    <input
                      className="form-control form-control-bordered search-input search-text"
                      name="search"
                      placeholder={t('licenseContent.placeholder')}
                      value={search}
                      onChange={handleChange}
                      onKeyDown={handleKeyDown}
                    />
                  </Row>
                </Col>
                <Col md={2} className="d-flex justify-content-start">
                  <Button
                    disabled={!search}
                    onClick={clickSearch}
                    className="search-btn"
                  >
                    {t('search')}
                  </Button>
                </Col>
              </Row>

              <Row className="m-0">
                <Col>
                  {!isProcessing && Object.keys(mainProductInfo).length > 0 && (
                    <BootstrapTable
                      keyField="ticketId"
                      data={[mainProductInfo]}
                      columns={columns}
                      wrapperClasses="table-license-content"
                    />
                  )}
                </Col>
              </Row>

              {!isProcessing && Object.keys(mainProductInfo).length === 0 && (
                <Row className="m-0">
                  <Col>
                    <NoDataFound />
                  </Col>
                </Row>
              )}

              {!isProcessing && Object.keys(mainProductInfo).length > 0 && (
                <Row className="mb-10 m-0 justify-content-center">
                  <Col>
                    <div className="div-tabs">
                      <TabsControl
                        activeKey={query.get('tab') || LICENSE_TABS.ACTIVE}
                        id="license-info"
                        tabs={tabs}
                        onTabChange={handleTabChange}
                      />
                    </div>
                  </Col>
                </Row>
              )}
            </Col>
          </Row>

          <Spinner2 show={isProcessing} />
        </Col>
      </Row>
    </>
  );
};
